import React, { useEffect, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
// Tree-shaking: Use path imports instead of barrel imports
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import logPortalRequest from "../../../utils/loggingUtil";
import COMMON_CONSTANTS from "../../../constants/commonConstants";
import WarningIcon from "../../../assets/icons/svg/warning.svg";
import popupHighlightImage from "../../../assets/popupHighlightImage.png";

function SessionExtensionPopup(props) {
  const { sessionIsAboutToExpire, expireTime, validateAndExtendToken, signout } = useAuthContext();
  const [isShowSessionAlert, setIsShowSessionAlert] = useState(false);
  const [remainingTime, setRemainingTime] = useState(120); // 2 minutes in seconds

  useEffect(() => {
    // Show popup when session is about to expire
    if (sessionIsAboutToExpire) {
      setIsShowSessionAlert(true);
    } else {
      setIsShowSessionAlert(false);
    }
  }, [sessionIsAboutToExpire]);

  useEffect(() => {
    let timer;

    if (isShowSessionAlert && expireTime) {
      //Following is to update the remaining time every second
      const updateRemainingTime = () => {
        const now = Date.now();
        const expTime = expireTime.getTime();
        const remaining = (Math.max(0, Math.floor((expTime - now) / 1000))) - 60;
        setRemainingTime(remaining);
        if (remaining <= 0) {
          logPortalRequest(
            {
              message: `Session expired - user did not extend session in time. Redirecting to sign out.`,
              portalType: COMMON_CONSTANTS?.PORTAL_TYPE_GENERAL,
            }
          );
          return;
        }
      };

      updateRemainingTime();

      timer = setInterval(updateRemainingTime, 1000);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [isShowSessionAlert, expireTime]);

  // Format remaining time as MM:SS
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    if (reason === 'timeout') {
      logPortalRequest(
        {
          message: `User did not respond to session warning. Redirected to Sign out.`,
          portalType: COMMON_CONSTANTS?.PORTAL_TYPE_GENERAL,
        }
      );
      signout();
      return;
    }

    setIsShowSessionAlert(false);
  };

  const handleExtendSession = () => {
    validateAndExtendToken();
    setIsShowSessionAlert(false);
  };

  return (
    isShowSessionAlert && (
      <Snackbar
        open={isShowSessionAlert}
        autoHideDuration={remainingTime * 1000}
        onClose={handleClose}
      >
        <Box sx={{
          width: 377,
          backgroundColor: 'background.white',
          boxShadow: '0px 3px 8px 0px #00000040',
          borderRadius: "5px",
          padding: "20px 16px 23px",
          position: "relative",
          '&::before': {
            content: '""',
            position: 'absolute',
            height: '6px',
            width: '100%',
            top: '0',
            left: '0',
            backgroundImage: `url(${popupHighlightImage})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          },
        }} >
          <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", marginBottom: "15px" }}>
            <img src={WarningIcon} alt="warning" />
            <Typography sx={{
              fontFamily: 'heading.fontFamily.primary',
              fontWeight: 700,
              fontSize: "26px !important",
              lineHeight: "32.5px",
              color: 'primary.main',
              marginLeft: "10px",
            }}>Warning:</Typography>
          </Box>
          <Typography sx={{
            fontFamily: 'body.fontFamily.primary',
            fontWeight: 400,
            fontSize: '16px !important',
            lineHeight: '24px',
            color: 'text.accent',
            '& a': {
              color: 'text.accent',
              textDecoration: 'none',
              cursor: 'pointer',
              fontWeight: 700,
            },
          }}>
            Due to inactivity, your session will expire in {formatTime(remainingTime)}. To extend your session for 60 minutes, please <a href="javascript:void(0)" onClick={handleExtendSession}>click here</a>.
          </Typography>
        </Box>
      </Snackbar >
    )
  );
}

export default SessionExtensionPopup;
